import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useEffect } from 'react';

export interface EventComment {
  id: string;
  event_id: string;
  user_id: string;
  comment_text: string;
  created_at: string;
  updated_at: string;
  user_email?: string;
}

// Fetch comments for an event
export const useEventComments = (eventId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['event-comments', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');

      const { data, error } = await supabase
        .from('event_comments')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching event comments:', error);
        throw error;
      }

      // Add placeholder emails (in production, join with profiles table)
      const commentsWithEmails = (data || []).map(comment => ({
        ...comment,
        user_email: `user-${comment.user_id.slice(0, 8)}@example.com`,
      }));

      return commentsWithEmails as EventComment[];
    },
    enabled: !!eventId,
  });

  // Subscribe to real-time comments
  useEffect(() => {
    if (!eventId) return;

    const channel = supabase
      .channel(`event-comments-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_comments',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['event-comments', eventId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, queryClient]);

  return query;
};

// Add comment to event
export const useAddEventComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, commentText }: { eventId: string; commentText: string }) => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User must be authenticated');
      }

      const { data, error } = await supabase
        .from('event_comments')
        .insert({
          event_id: eventId,
          user_id: user.id,
          comment_text: commentText,
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding comment:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-comments', variables.eventId] });
      toast.success('Comment added');
    },
    onError: (error) => {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    },
  });
};

// Delete comment
export const useDeleteEventComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ commentId, eventId }: { commentId: string; eventId: string }) => {
      const { error } = await supabase
        .from('event_comments')
        .delete()
        .eq('id', commentId);

      if (error) {
        console.error('Error deleting comment:', error);
        throw error;
      }

      return { eventId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-comments', data.eventId] });
      toast.success('Comment deleted');
    },
    onError: (error) => {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    },
  });
};
